/**
 * Operational Workspace Tokens — Semantic CSS classes for the
 * three-zone patient/entity workspace layout system.
 *
 * All values map to CSS variables (--ws-*) defined in index.css.
 * Automatically respects light/dark mode.
 *
 * Usage:
 *   import { wsTokens } from '@/design-system/tokens/workspaceTokens';
 *   <div className={wsTokens.page}>...</div>
 */

// ── Page-level tokens ─────────────────────────────────────────
export const wsTokens = {
  /** Full-page workspace shell. */
  page: 'ops-workspace',
  /** Page header card (patient identity, actions). */
  header: 'ops-header',
  /** Smart action / recommendation row. */
  actionRow: 'ops-action-row',
  /** Content shell — splits into center + right context panel on xl+. */
  contentShell: 'ops-content-shell',
  /** Center (main) content column. */
  center: 'ops-center',
  /** Right context panel column. */
  context: 'ops-context',
  /** Section card — standard content block. */
  section: 'ops-section',
  /** Section header row. */
  sectionHeader: 'ops-section__header',
  /** Section title text. */
  sectionTitle: 'ops-section__title',
  /** Section body area. */
  sectionBody: 'ops-section__body',
  /** Right-panel context card. */
  ctxCard: 'ops-ctx-card',
  ctxCardHeader: 'ops-ctx-card__header',
  ctxCardTitle: 'ops-ctx-card__title',
  ctxCardBody: 'ops-ctx-card__body',
  /** Inline tab navigation bar. */
  inlineNav: 'ops-inline-nav',
  inlineNavItem: 'ops-inline-nav__item',
  inlineNavItemActive: 'ops-inline-nav__item--active',
  /** Dual-card responsive grid (50/50 on md+). */
  dualCards: 'ops-dual-cards',
  /** Quick action button. */
  quickAction: 'ops-quick-action',
} as const;

// ── Stat card variant classes ─────────────────────────────────
export const wsStatVariants = {
  blue: 'ops-stat ops-stat--blue',
  green: 'ops-stat ops-stat--green',
  purple: 'ops-stat ops-stat--purple',
  orange: 'ops-stat ops-stat--orange',
  red: 'ops-stat ops-stat--red',
} as const;

export type WsStatVariant = keyof typeof wsStatVariants;

// ── Stepper classes ───────────────────────────────────────────
export const wsStepperTokens = {
  container: 'ops-stepper',
  step: 'ops-stepper__step',
  stepDone: 'ops-stepper__step--done',
  stepActive: 'ops-stepper__step--active',
  dot: 'ops-stepper__dot',
  dotDone: 'ops-stepper__dot--done',
  dotActive: 'ops-stepper__dot--active',
  connector: 'ops-stepper__connector',
  connectorDone: 'ops-stepper__connector--done',
} as const;

// ── Progress bar classes ──────────────────────────────────────
export const wsProgressTokens = {
  bar: 'ops-progress',
  fill: 'ops-progress__fill',
  success: 'ops-progress--success',
  warning: 'ops-progress--warning',
  danger: 'ops-progress--danger',
} as const;

// ── Timeline classes ──────────────────────────────────────────
export const wsTimelineTokens = {
  container: 'ops-timeline',
  item: 'ops-timeline__item',
  marker: 'ops-timeline__marker',
  dot: 'ops-timeline__dot',
  line: 'ops-timeline__line',
  content: 'ops-timeline__content',
} as const;

// ── Activity feed classes ─────────────────────────────────────
export const wsFeedTokens = {
  item: 'ops-feed-item',
  icon: 'ops-feed-icon',
  body: 'ops-feed-body',
} as const;
